// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getAPIKeys } from '@/account/api/get-api-keys';
import { SparseOrganization } from '@/account/api/types';
import { toPermissionLabel } from '@/account/utilities/to-permission-label';
import {
  Alert,
  Box,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

interface Props {
  organization: SparseOrganization;
}

export function APIKeyList(props: Props) {
  const { data, isError } = useQuery({
    queryKey: ['organization', props.organization.key, 'api-keys'],
    queryFn: () => getAPIKeys(props.organization.key),
  });

  if (isError) {
    return (
      <Alert severity="error">
        Could not load the API keys for the <b>{props.organization.name}</b>{' '}
        organization
      </Alert>
    );
  }

  const apiKeys =
    data?.keys.slice().sort((key, other) => {
      if (key.label === null && other.label === null) {
        return 0;
      }

      if (key.label === null) {
        return 1;
      }

      if (other.label === null) {
        return -1;
      }

      return key.label.localeCompare(other.label);
    }) ?? [];

  return (
    <>
      {apiKeys.length === 0 ? (
        <Box>
          <Typography>
            The <b>{props.organization.name}</b> organization does not have an
            API key configured.
          </Typography>
          <Typography marginTop={1}>
            Please contact{' '}
            <Link href="mailto:support@hardfin.com">support@hardfin.com</Link>{' '}
            to set up API access to the Hardfin platform.
          </Typography>
        </Box>
      ) : (
        <>
          <Divider />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="30%">Name</TableCell>
                <TableCell width="20%">Access</TableCell>
                <TableCell width="25%">Public value</TableCell>
                <TableCell width="25%">Secret value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell width="30%">{key.label}</TableCell>
                  <TableCell width="20%">
                    {toPermissionLabel(key.scopes)}
                  </TableCell>
                  <TableCell width="25%">{key.key}</TableCell>
                  <TableCell width="25%">
                    <Tooltip
                      arrow
                      placement="top"
                      title="Secret values can only be viewed at creation"
                    >
                      <Typography sx={{ width: 'fit-content' }}>
                        {'•'.repeat(16)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography marginX={2} marginY={2}>
            Please contact{' '}
            <Link href="mailto:support@hardfin.com">support@hardfin.com</Link>{' '}
            to update API access to the Hardfin platform.
          </Typography>
          <Divider />
        </>
      )}
    </>
  );
}
